import * as back from '~/components/layouts/Navigation/BreadcrumbContainer/functional/backLinks';
import { documentTemplateTitle } from '~/components/layouts/Navigation/BreadcrumbContainer/functional/documentTemplateTitle';
import {
  safeUnit,
  safeApplication,
  crudBreadcrumb,
  detailBreadcrumb,
  listBreadcrumb,
} from '~/components/layouts/Navigation/BreadcrumbContainer/functional/generators';
import * as match from '~/components/layouts/Navigation/BreadcrumbContainer/functional/matchers';
import { getTenantFullName } from '~/components/tenants';
import { emptyPlaceholder } from '~/utils/constant';
import type { RouteRule, BreadcrumbContext } from '~/components/layouts/Navigation/BreadcrumbContainer/functional/types';

const PRIORITY = {
  SECTION_BASE: 200,
  LIST_PAGE: 400,
  ADD_FORM: 500,
  EDIT_FORM: 510,
  DETAIL_PAGE: 520,
  SPECIAL_ACTION: 530,
} as const;

const propertyUnitActionBreadcrumb = (actionLabel: string) => (context: BreadcrumbContext) => {
  const property = context.currentProperty?.();
  const unit = safeUnit(context);
  const isLoaded = context.isLoaded;

  if (!property || !isLoaded) {
    return [
      { label: context.t('Loading') },
      { label: context.t('Loading') },
      { label: context.t('Loading') },
      { label: context.t(actionLabel) },
    ];
  }

  const portfolioId = property.portfolio?.id;
  const propertyId = property.id;
  const unitId = unit.id;
  const portfolioName = property.portfolio?.name;
  const propertyName = property.name;
  const unitName = unit.name;

  return [
    { label: portfolioName || context.t('Loading'), link: `/portfolios/${portfolioId}` },
    { label: propertyName || context.t('Loading'), link: `/portfolios/${portfolioId}/properties/${propertyId}` },
    { label: unitName || context.t('Loading'), link: `/portfolios/${portfolioId}/properties/${propertyId}/units/${unitId}` },
    { label: context.t(actionLabel) },
  ];
};

export const leasingRules: RouteRule[] = [
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/recurring/payment/')),
    generate: propertyUnitActionBreadcrumb('Set up recurring offline payment'),
    backLink: back.goBack,
    priority: PRIORITY.SPECIAL_ACTION + 10,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/recurring/charge/')),
    generate: propertyUnitActionBreadcrumb('Set up recurring charge'),
    backLink: back.goBack,
    priority: PRIORITY.SPECIAL_ACTION + 10,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/recurring/subsidies/')),
    generate: propertyUnitActionBreadcrumb('Set up subsidy'),
    backLink: back.goBack,
    priority: PRIORITY.SPECIAL_ACTION + 10,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/recurring/credit/')),
    generate: propertyUnitActionBreadcrumb('Set up recurring credit'),
    backLink: back.goBack,
    priority: PRIORITY.SPECIAL_ACTION + 10,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/edit-lease')),
    generate: propertyUnitActionBreadcrumb('Edit lease'),
    backLink: back.goBack,
    priority: PRIORITY.EDIT_FORM,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/create-lease-draft')),
    generate: propertyUnitActionBreadcrumb('Create lease document'),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/renewal-of-lease')),
    generate: propertyUnitActionBreadcrumb('Add renewal offer'),
    backLink: back.goBack,
    priority: PRIORITY.SPECIAL_ACTION,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/draft/')),
    generate: (context) => {
      const property = context.currentProperty?.();
      const unit = safeUnit(context);
      const isLoaded = context.isLoaded;

      const pathParts = context.path.split('/');
      const leaseIndex = pathParts.indexOf('leases');
      const leaseId = leaseIndex !== -1 ? pathParts[leaseIndex + 1] : '';
      const draftIndex = pathParts.indexOf('draft');
      const draftId = draftIndex !== -1 ? pathParts[draftIndex + 1] : '';

      if (!property || !isLoaded) {
        return [
          { label: context.t('Loading') },
          { label: context.t('Loading') },
          { label: context.t('Loading') },
          { label: context.t('Signings'), link: `/leasing/leases/${leaseId}/signings` },
          { label: draftId },
        ];
      }

      const portfolioId = property.portfolio?.id;
      const propertyId = property.id;
      const unitId = unit.id;
      const portfolioName = property.portfolio?.name;
      const propertyName = property.name;
      const unitName = unit.name;

      return [
        { label: portfolioName || context.t('Loading'), link: `/portfolios/${portfolioId}` },
        { label: propertyName || context.t('Loading'), link: `/portfolios/${portfolioId}/properties/${propertyId}` },
        { label: unitName || context.t('Loading'), link: `/portfolios/${portfolioId}/properties/${propertyId}/units/${unitId}` },
        { label: context.t('Signings'), link: `/leasing/leases/${leaseId}/signings` },
        { label: draftId },
      ];
    },
    backLink: back.goBack,
    priority: PRIORITY.SPECIAL_ACTION + 50,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.regex(/\/leases\/\d{5,}/)),
    generate: propertyUnitActionBreadcrumb('Lease detail'),
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/leases/new')),
    generate: (context) => [
      { label: context.t('Leasing') },
      { label: context.t('Leases'), link: '/leasing/leases' },
      { label: context.t('Set up a new lease'), link: '/leasing/leases/new' },
    ],
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/lease-templates/signing/')),
    generate: (context) => [
      { label: context.t('Leasing') },
      { label: context.t('Lease templates'), link: '/leasing/lease-templates' },
      { label: documentTemplateTitle() || context.t('Loading') },
    ],
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.regex(/\/lease-templates\/\d{5,}/)),
    generate: (context) => [
      { label: context.t('Leasing') },
      { label: context.t('Lease templates'), link: '/leasing/lease-templates' },
      { label: context.presentableTitle()?.title || context.t('Loading') },
    ],
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: (path) => path === '/magic-tags/add' || path.startsWith('/magic-tags/add?'),
    generate: (context) => {
      return [{ label: context.t('Home'), link: '/' }, { label: context.t('Add magic tag') }];
    },
    backLink: back.goBack,
    priority: PRIORITY.SPECIAL_ACTION,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/leases'), match.regex(/\/\d{5,}\/setup-recurring-charge/)),
    generate: propertyUnitActionBreadcrumb('Set up recurring charge'),
    backLink: back.goBack,
    priority: PRIORITY.SPECIAL_ACTION,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/leases'), match.regex(/\/\d{5,}\/setup-recurring-credit/)),
    generate: propertyUnitActionBreadcrumb('Set up recurring credit'),
    backLink: back.goBack,
    priority: PRIORITY.SPECIAL_ACTION,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/leases'), match.regex(/\/\d{5,}\/setup-recurring-payment/)),
    generate: propertyUnitActionBreadcrumb('Set up recurring offline payment'),
    backLink: back.goBack,
    priority: PRIORITY.SPECIAL_ACTION,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/leases'), match.regex(/\/\d{5,}\/setup-subsidy/)),
    generate: propertyUnitActionBreadcrumb('Set up subsidy'),
    backLink: back.goBack,
    priority: PRIORITY.SPECIAL_ACTION,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/lease-templates')),
    generate: listBreadcrumb('Leasing', '/leasing', 'Lease templates', '/leasing/lease-templates'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/rental-applications'), match.regex(/\d{5,}/)),
    generate: (context) => {
      const rentalPath = '/leasing/rental-applications';
      const isDraft = context.path.includes('draft-rental-applications');
      return [
        { label: context.t('Leasing') },
        { label: context.t('Rental applications'), link: rentalPath },
        {
          label: isDraft ? context.t('Unfinished') : context.t('Submitted'),
          link: `${rentalPath}${isDraft ? '/draft-rental-applications' : ''}`,
        },
        { label: getTenantFullName(safeApplication(context)) || emptyPlaceholder },
      ];
    },
    backLink: back.goBack,
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/rental-applications'), match.includes('/new')),
    generate: crudBreadcrumb('Leasing', '/leasing', 'Rental applications', '/leasing/rental-applications', 'New rental application'),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/rental-applications')),
    generate: listBreadcrumb('Leasing', '/leasing', 'Rental applications', '/leasing/rental-applications'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/add-offline-payment')),
    generate: listBreadcrumb('Leasing', '/leasing', 'Add offline payments', '/leasing/add-offline-payment'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/subsidizers'), match.regex(/\/\d{5,}\/edit/)),
    generate: crudBreadcrumb('Leasing', '/leasing', 'Subsidizers', '/leasing/subsidizers', 'Edit subsidizer'),
    backLink: back.goBack,
    priority: PRIORITY.EDIT_FORM,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/subsidizers'), match.includes('/add')),
    generate: crudBreadcrumb('Leasing', '/leasing', 'Subsidizers', '/leasing/subsidizers', 'Add subsidizer'),
    backLink: back.goBack,
    priority: PRIORITY.ADD_FORM,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/subsidizers')),
    generate: listBreadcrumb('Leasing', '/leasing', 'Subsidizers', '/leasing/subsidizers'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },

  {
    match: match.and(match.startsWith('/leasing'), match.includes('/lease-renewal')),
    generate: listBreadcrumb('Leasing', '/leasing', 'Lease renewal', '/leasing/lease-renewal'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/insurance'), match.includes('/notice-campaigns')),
    generate: detailBreadcrumb('Leasing', '/leasing', 'Insurance', '/leasing/insurance', 'Notice campaign'),
    backLink: back.custom('/leasing/insurance'),
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/insurance'), match.includes('/externals/')),
    generate: detailBreadcrumb('Leasing', '/leasing', 'Insurance', '/leasing/insurance', 'External insurance details'),
    backLink: back.custom('/leasing/insurance/externals'),
    priority: PRIORITY.DETAIL_PAGE,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/insurance'), match.includes('/externals')),
    generate: detailBreadcrumb('Leasing', '/leasing', 'Insurance', '/leasing/insurance', 'External insurance management'),
    backLink: back.custom('/leasing/insurance'),
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/insurance'), match.includes('/damage-waiver/policies')),
    generate: detailBreadcrumb('Leasing', '/leasing', 'Insurance', '/leasing/insurance', 'Damage Waiver policies'),
    backLink: back.custom('/leasing/insurance'),
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/insurance'), match.includes('/sures')),
    generate: detailBreadcrumb('Leasing', '/leasing', 'Insurance', '/leasing/insurance', 'Sure policy list'),
    backLink: back.custom('/leasing/insurance'),
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.and(match.startsWith('/leasing'), match.includes('/insurance')),
    generate: listBreadcrumb('Leasing', '/leasing', 'Insurance', '/leasing/insurance'),
    backLink: back.homeLink,
    priority: PRIORITY.LIST_PAGE,
  },
  {
    match: match.startsWith('/leasing'),
    generate: (context) => [{ label: context.t('Leasing') }],
    backLink: back.homeLink,
    priority: PRIORITY.SECTION_BASE,
  },
];
